/**
 * Screen Size Debugger
 * Displays current screen dimensions and matching breakpoint
 */

class ScreenDebugger {
    constructor() {
        this.debugElement = null;
        this.init();
    }

    init() {
        // Create debug display element
        this.debugElement = document.createElement('div');
        this.debugElement.id = 'screen-debugger';
        this.debugElement.style.cssText = `
            position: fixed;
            top: 10px;
            left: 10px;
            background: rgba(0, 0, 0, 0.85);
            color: #00ff00;
            padding: 12px 16px;
            border-radius: 8px;
            font-family: 'Courier New', monospace;
            font-size: 13px;
            z-index: 9999;
            line-height: 1.6;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
            min-width: 320px;
            display: none;
        `;
        document.body.appendChild(this.debugElement);

        // Update on load and resize
        this.update();
        window.addEventListener('resize', () => this.update());
    }

    getBreakpointInfo() {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const orientation = width > height ? 'landscape' : 'portrait';
        
        let breakpoint = 'Unknown';
        let mediaQuery = '';

        // Check breakpoints in order of specificity
        if (width >= 360 && width <= 370 && height >= 720 && height <= 760 && orientation === 'portrait') {
            breakpoint = 'Samsung Galaxy S8+ Portrait';
            mediaQuery = '@media (min-width: 360px) and (max-width: 370px) and (min-height: 720px) and (max-height: 760px) and (orientation: portrait)';
        } else if (width >= 720 && width <= 760 && height >= 360 && height <= 370 && orientation === 'landscape') {
            breakpoint = 'Samsung Galaxy S8+ Landscape';
            mediaQuery = '@media (min-width: 720px) and (max-width: 760px) and (min-height: 360px) and (max-height: 370px) and (orientation: landscape)';
        } else if (width >= 375 && width <= 389 && orientation === 'portrait') {
            breakpoint = 'iPhone SE Portrait';
            mediaQuery = '@media (min-width: 375px) and (max-width: 389px) and (orientation: portrait)';
        } else if (height <= 400 && orientation === 'landscape') {
            breakpoint = 'iPhone SE Landscape';
            mediaQuery = '@media (max-height: 400px) and (orientation: landscape)';
        } else if (width >= 390 && width <= 429 && orientation === 'portrait') {
            breakpoint = 'iPhone 12/13/14 Portrait';
            mediaQuery = '@media (min-width: 390px) and (max-width: 429px) and (orientation: portrait)';
        } else if (width >= 390 && width <= 430 && orientation === 'portrait') {
            breakpoint = 'iPhone XR Portrait';
            mediaQuery = '@media (min-width: 390px) and (max-width: 430px) and (orientation: portrait)';
        } else if (width >= 428 && width <= 432 && orientation === 'portrait') {
            breakpoint = 'iPhone 14 Pro Max Portrait';
            mediaQuery = '@media (min-width: 428px) and (max-width: 432px) and (orientation: portrait)';
        } else if (width >= 430 && width <= 450 && orientation === 'portrait') {
            breakpoint = 'iPhone 14 Pro Max Portrait (Scaled)';
            mediaQuery = '@media (min-width: 430px) and (max-width: 450px) and (orientation: portrait)';
        } else if (width >= 768 && width <= 1023 && orientation === 'landscape') {
            breakpoint = 'iPhone XR Landscape';
            mediaQuery = '@media (min-width: 768px) and (max-width: 1023px) and (orientation: landscape)';
        } else if (width >= 930 && width <= 934 && height <= 432 && orientation === 'landscape') {
            breakpoint = 'iPhone 14 Pro Max Landscape';
            mediaQuery = '@media (min-width: 930px) and (max-width: 934px) and (max-height: 432px) and (orientation: landscape)';
        } else if (width >= 1024) {
            breakpoint = 'Desktop (1024px+)';
            mediaQuery = '@media (min-width: 1024px)';
        } else if (width >= 768 && width <= 1023) {
            breakpoint = 'Tablet';
            mediaQuery = '@media (min-width: 768px) and (max-width: 1023px)';
        } else if (width < 768) {
            breakpoint = 'Mobile';
            mediaQuery = '@media (max-width: 767px)';
        }

        return { breakpoint, mediaQuery };
    }

    update() {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const orientation = width > height ? 'landscape' : 'portrait';
        const { breakpoint, mediaQuery } = this.getBreakpointInfo();
        
        // Get computed font size
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const bodyFontSize = parseFloat(getComputedStyle(document.body).fontSize);

        this.debugElement.innerHTML = `
            <div style="font-weight: bold; color: #ffff00; margin-bottom: 8px;">📐 Screen Debug Info</div>
            <div><strong>Width:</strong> ${width}px</div>
            <div><strong>Height:</strong> ${height}px</div>
            <div><strong>Orientation:</strong> ${orientation}</div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #444;">
                <strong style="color: #00ffff;">Breakpoint:</strong><br/>
                <span style="color: #ff9900;">${breakpoint}</span>
            </div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #444;">
                <strong style="color: #00ff00;">Font Sizes:</strong><br/>
                <span style="color: #aaa;">Root: ${rootFontSize}px (1rem)</span><br/>
                <span style="color: #aaa;">Body: ${bodyFontSize}px</span>
            </div>
            <div style="margin-top: 8px; font-size: 11px; color: #aaa; word-wrap: break-word;">
                ${mediaQuery}
            </div>
        `;
    }
    
    destroy() {
        if (this.debugElement) {
            this.debugElement.remove();
        }
    }
    
    toggle() {
        if (this.debugElement) {
            const isHidden = this.debugElement.style.display === 'none';
            this.debugElement.style.display = isHidden ? 'block' : 'none';
        }
    }
}

// Auto-initialize when script loads
let screenDebuggerInstance = null;

// Export for manual control
export function enableScreenDebugger() {
    if (!screenDebuggerInstance) {
        screenDebuggerInstance = new ScreenDebugger();
    }
}

export function disableScreenDebugger() {
    if (screenDebuggerInstance) {
        screenDebuggerInstance.destroy();
        screenDebuggerInstance = null;
    }
}

export function toggleScreenDebugger() {
    if (screenDebuggerInstance) {
        screenDebuggerInstance.toggle();
    }
}

// Auto-start in development mode (hidden by default)
if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
    enableScreenDebugger();
    
    // Add keyboard listener for 'd' key to toggle visibility
    window.addEventListener('keydown', (event) => {
        if (event.key === 'd' || event.key === 'D') {
            toggleScreenDebugger();
        }
    });
}
